import React, { useEffect, useState } from "react";
import FsLightbox from "fslightbox-react";
import Auth from "../../utils/auth";
import { FaDownload, FaShareAlt, FaEye, FaTrashAlt } from "react-icons/fa";
import { API_URL } from "../../api/APIUrl";

const GalleryItemVideo = ({ data }) => {
  const [toggler, setToggler] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [tempUrl, setTempUrl] = useState(null);

  useEffect(() => {
    // Check administrative flags securely
    if (Auth.isAdmin() !== "false" && Auth.isAdmin() !== false) {
      setIsAdmin(true);
    }
  }, []);

  // Fetch the presigned URL dynamically
  const generateMaterialUrl = async (id) => {
    try {
      const res = await fetch(`${API_URL}/api/materials/${id}/download`);
      const responseData = await res.json();
      return responseData.url;
    } catch (err) {
      console.error("Failed to generate URL:", err);
      return null;
    }
  };

  const deletePost = async (id) => {
    await fetch(`${API_URL}/api/posts/${id}`, {
      method: "DELETE",
    });
    window.location.reload();
  };

  const confirmDelete = async (id) => {
    const confirm = window.confirm(
      "Are you sure you want to delete this material? This action cannot be undone."
    );
    if (confirm) {
      await deletePost(id);
    }
  };

  // Wait for the signed URL before downloading
  const handleDownload = async (id) => {
    const url = await generateMaterialUrl(id);
    if (url) {
      window.open(url, "_blank");
    } else {
      alert("No download URL available.");
    }
  };

  // Wait for the signed URL before sharing
  const handleShare = async (id, title = "Check this out") => {
    const url = await generateMaterialUrl(id);
    if (!url) {
      alert("No shareable URL received.");
      return;
    }

    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

    if (isMobile && navigator.share) {
      try {
        await navigator.share({
          title,
          text: "Please click on the link to view or download the file.",
          url,
        });
      } catch (err) {
        if (err.name !== "AbortError") console.error("Share failed:", err);
      }
    } else {
      const subject = encodeURIComponent(`File Share: ${title}`);
      const body = encodeURIComponent(
        `Please click on the link to view or download the file: ${title}\n\nLink:\n${url}`
      );
      window.location.href = `mailto:?subject=${subject}&body=${body}`;

      try {
        await navigator.clipboard.writeText(url);
        alert("Opening Email App... (Link also copied to clipboard!)");
      } catch (err) {
        console.error("Clipboard failed:", err);
      }
    }
  };

  // Wait for the signed URL before opening the Lightbox
  const handleView = async (id) => {
    const url = await generateMaterialUrl(id);
    if (url) {
      setTempUrl(url);
      setToggler(!toggler);
    }
  };

  // --- COMPONENT INLINE STYLES ---
  const imageWrapperStyle = {
    position: "relative",
    width: "100%",
    paddingTop: "133%", // Clean 3:4 Aspect Ratio layout
    backgroundColor: "#f1f5f9",
    overflow: "hidden",
  };

  const thumbnailStyle = {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
  };

  const bodyStyle = {
    padding: "16px",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    justifyContent: "space-between",
  };

  const actionButtonStyle = {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flex: 1,
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    backgroundColor: "#ffffff",
    color: "#475569",
    fontSize: "14px",
    cursor: "pointer",
    transition: "all 0.2s ease",
  };

  return (
    <>
      <div
        className="h-100 d-flex flex-column"
        style={{ width: "100%" }}
        onMouseEnter={(e) => {
          const media = e.currentTarget.querySelector(".catalog-thumb");
          if (media) media.style.transform = "scale(1.04)";
        }}
        onMouseLeave={(e) => {
          const media = e.currentTarget.querySelector(".catalog-thumb");
          if (media) media.style.transform = "scale(1.0)";
        }}
      >
        {/* Media Frame Canvas */}
        <div style={imageWrapperStyle}>
          {/* Changed from <video> to <img> to handle the S3 image thumbnail */}
          <img
            className="catalog-thumb"
            src={data.thumbnailUrl}
            alt={data.postTitle}
            style={thumbnailStyle}
          />

          {/* Contextual Floating Admin Controls */}
          {isAdmin && (
            <button
              onClick={() => confirmDelete(data._id)}
              style={{
                position: "absolute",
                top: "12px",
                right: "12px",
                zIndex: 5,
                backgroundColor: "rgba(239, 68, 68, 0.9)",
                color: "#ffffff",
                border: "none",
                borderRadius: "50%",
                width: "36px",
                height: "36px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                cursor: "pointer",
              }}
              title="Delete Material Entry"
            >
              <FaTrashAlt size={14} />
            </button>
          )}
        </div>

        {/* Dynamic Typography Meta Container */}
        <div style={bodyStyle}>
          <div className="mb-3">
            {data.category && (
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  color: "#3b82f6",
                  display: "block",
                  marginBottom: "4px",
                }}
              >
                {data.category}
              </span>
            )}
            <h5
              style={{
                fontSize: "15px",
                fontWeight: "600",
                color: "#1e293b",
                margin: 0,
                lineHeight: "1.4",
                textTransform: "capitalize",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {data.postTitle}
            </h5>
          </div>

          {/* Action Control Button Strip */}
          <div className="d-flex gap-2 mt-auto">
            <button
              style={actionButtonStyle}
              onClick={() => handleDownload(data._id)}
              title="Download Document File"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f8fafc";
                e.currentTarget.style.color = "#007bff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.color = "#475569";
              }}
            >
              <FaDownload />
            </button>
            <button
              style={actionButtonStyle}
              onClick={() => handleShare(data._id, data.postTitle)}
              title="Share File Link"
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = "#f8fafc";
                e.currentTarget.style.color = "#007bff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = "#ffffff";
                e.currentTarget.style.color = "#475569";
              }}
            >
              <FaShareAlt />
            </button>
            <button
              style={{
                ...actionButtonStyle,
                backgroundColor: "#007bff",
                color: "#ffffff",
                border: "1px solid #007bff",
              }}
              onClick={() => handleView(data._id)}
              title="Quick Preview Viewer"
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#0056b3")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#007bff")
              }
            >
              <FaEye className="me-2" /> View
            </button>
          </div>
        </div>
      </div>

      {/* Embedded Lightbox Gateway */}
      {tempUrl && <FsLightbox toggler={toggler} sources={[tempUrl]} />}
    </>
  );
};

export default GalleryItemVideo;
